import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../../services/DataRequestService";
import { get_workflows } from "../../services/ApiManageWorkflow";
import { convertServerTimeToLocal } from "../../Utils/helper";
import Loader from "../common/Loader";
import PageHeader from "../common/PageHeader";

function SupplyChainList() {
  const navigate = useNavigate();
  const [workFlowdata, setWorkFlowData] = useState([]);
  const [masterWorkFlowdata, setMasterWorkFlowdata] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingWorkflow, setGeneratingWorkflow] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    getData();
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const getData = async () => {
    setIsLoading(true);
    try {
      let _response = await get_workflows(getCookie("sub"));
      _response = _response || [];
      _response.reverse();
      setWorkFlowData(_response);
      setMasterWorkFlowdata(_response);
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
    }
    setIsLoading(false);
  };

  const searchByName = (name) => {
    if (name !== "") {
      const newData = masterWorkFlowdata.filter((workflow) =>
        workflow.workflow_name?.toLowerCase().includes(name.toLowerCase())
      );
      setWorkFlowData(newData);
      setCurrentPage(1);
    } else {
      setWorkFlowData(masterWorkFlowdata);
      setCurrentPage(1);
    }
  };

  const sortedData = [...workFlowdata].sort((a, b) => {
    const da = a.process_date ? new Date(a.process_date) : 0;
    const db = b.process_date ? new Date(b.process_date) : 0;
    return db - da;
  });

  const totalPages = Math.ceil((sortedData.length || 0) / itemsPerPage);
  const start = (currentPage - 1) * itemsPerPage;
  const currentData = sortedData.slice(start, start + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = (page, total) => {
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(total, startPage + 4);
    if (endPage - startPage + 1 < 5) {
      startPage = Math.max(1, endPage - 4);
    }
    return Array.from(
      { length: Math.max(0, endPage - startPage + 1) },
      (_, i) => startPage + i
    );
  };

  const openDetails = (workflow) => {
    setIsGenerating(true);
    setGeneratingWorkflow(workflow?.workflow_name || "workflow");
    window.setTimeout(() => {
      navigate("/SupplyChainManagement/detail", { state: { workflow } });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      setIsGenerating(false);
      setGeneratingWorkflow("");
    }, 900);
  };

  if (isLoading) {
    return (
      <center
        className="flex flex-col items-center justify-center"
        style={{ height: "100vh" }}
      >
        <Loader size={"large"} />
      </center>
    );
  }

  return (
    <div style={pageStyles.wrapper}>
      {isGenerating && (
        <div style={pageStyles.overlay}>
          <div style={pageStyles.overlayCard}>
            <div style={pageStyles.spinner} />
            <p style={pageStyles.overlayTitle}>Generating Supply Plan...</p>
            <p style={pageStyles.overlaySub}>{generatingWorkflow}</p>
          </div>
        </div>
      )}

      <div style={pageStyles.inner}>
        <PageHeader
          title="Supply Chain Management"
          subtitle="Select a forecast workflow to open the SCM planning flow."
        />

        {/* Search */}
        <div style={pageStyles.filterBar}>
          <div style={pageStyles.searchGroup}>
            <label htmlFor="workflowName" style={pageStyles.searchLabel}>
              Search By Name
            </label>
            <input
              id="workflowName"
              onChange={(e) => searchByName(e.target.value)}
              placeholder="Type workflow name..."
              style={pageStyles.searchInput}
            />
          </div>
        </div>

        {/* Table */}
        <div style={pageStyles.tableWrap}>
          <div style={pageStyles.tableScroll}>
          <table style={pageStyles.table}>
            <thead>
              <tr>
                <th style={pageStyles.th}>Workflow Name</th>
                <th style={pageStyles.th}>Workflow Description</th>
                <th style={pageStyles.th}>Granularity</th>
                <th style={pageStyles.th}>Process Time</th>
                <th style={pageStyles.th}>Process Date</th>
                <th style={pageStyles.th}>Model Status</th>
                <th style={pageStyles.th}>Approval Status</th>
                <th style={pageStyles.thLast}>Supply Plan</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((elem, index) => (
                  <tr key={index} style={pageStyles.tr}>
                    <td style={pageStyles.tdBold}>{elem.workflow_name}</td>
                    <td style={pageStyles.tdDesc}>
                      {elem.workflow_description}
                    </td>
                    <td style={pageStyles.td}>{elem.granularity}</td>
                    <td style={pageStyles.td}>
                      {elem.process_date &&
                        convertServerTimeToLocal(elem.process_date)}
                    </td>
                    <td style={pageStyles.td}>
                      {elem.process_date?.split("T")[0]}
                    </td>
                    <td style={pageStyles.tdCenter}>
                      <span
                        style={{
                          ...pageStyles.badge,
                          backgroundColor:
                            elem.status?.toLowerCase() === "completed"
                              ? "#368E60"
                              : elem.status?.toLowerCase() === "processing"
                                ? "#E06639"
                                : "#973848",
                        }}
                      >
                        {elem.status}
                      </span>
                    </td>
                    <td style={pageStyles.tdCenter}>
                      <span
                        style={{
                          ...pageStyles.badge,
                          backgroundColor:
                            elem.approval_status?.toLowerCase() === "approved"
                              ? "#368E60"
                              : "#6a7d8c",
                        }}
                      >
                        {elem.approval_status}
                      </span>
                    </td>
                    <td style={pageStyles.tdCenter}>
                      {elem.status?.toLowerCase() === "completed" ? (
                        <button
                          type="button"
                          onClick={() => openDetails(elem)}
                          disabled={isGenerating}
                          style={{
                            ...pageStyles.detailsLink,
                            opacity: isGenerating ? 0.7 : 1,
                            cursor: isGenerating ? "not-allowed" : "pointer",
                          }}
                        >
                          Generate
                        </button>
                      ) : (
                        <span style={pageStyles.emptyCell}>-</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={pageStyles.emptyRow}>
                    No workflows found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination */}
        <div style={pageStyles.pagination}>
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={pageStyles.pageBtn}
          >
            Previous
          </button>

          {getVisiblePages(currentPage, totalPages).map((pageNum) => (
            <button
              key={pageNum}
              type="button"
              onClick={() => handlePageChange(pageNum)}
              style={{
                ...pageStyles.pageBtn,
                ...(currentPage === pageNum ? pageStyles.pageBtnActive : {}),
              }}
            >
              {pageNum}
            </button>
          ))}

          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            style={pageStyles.pageBtn}
          >
            Next
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes scmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}
      </style>
    </div>
  );
}

const pageStyles = {
  wrapper: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
  },
  inner: {
    width: "100%",
    margin: "0 auto",
    color: "#0c3c54",
    display: "flex",
    flexDirection: "column",
    padding: "8px 4px 20px",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(12, 60, 84, 0.55)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  overlayCard: {
    background: "#fff",
    border: "1px solid #e6eef4",
    borderRadius: 12,
    padding: "28px 36px",
    boxShadow: "0 12px 32px rgba(12, 60, 84, 0.18)",
    textAlign: "center",
    minWidth: 300,
  },
  spinner: {
    width: 40,
    height: 40,
    margin: "0 auto 14px",
    borderRadius: "50%",
    border: "4px solid #e6eef4",
    borderTopColor: "#E06639",
    animation: "scmSpin 0.8s linear infinite",
  },
  overlayTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: "#0c3c54",
    fontFamily: "Poppins, sans-serif",
  },
  overlaySub: {
    margin: "6px 0 0",
    fontSize: 13,
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
    marginBottom: 20,
  },
  searchGroup: {
    flex: "1 1 280px",
    maxWidth: 360,
  },
  searchLabel: {
    display: "block",
    fontSize: 14,
    fontWeight: 700,
    color: "#0c3c54",
    marginBottom: 8,
    fontFamily: "Poppins, sans-serif",
  },
  searchInput: {
    width: "100%",
    height: 40,
    padding: "0 12px",
    border: "1px solid #e6eef4",
    borderRadius: 8,
    fontSize: 14,
    fontFamily: "Poppins, sans-serif",
    color: "#0c3c54",
    background: "#fff",
    boxSizing: "border-box",
    outline: "none",
  },
  tableWrap: {
    width: "100%",
    background: "#032B4E",
    border: "3px solid #032B4E",
    borderRadius: 12,
    boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    overflow: "hidden",
    marginBottom: 16,
  },
  tableScroll: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 980,
    background: "#fff",
  },
  th: {
    textAlign: "center",
    padding: "12px 16px",
    backgroundColor: "#032B4E",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    whiteSpace: "nowrap",
    border: "1px solid #032B4E",
  },
  thLast: {
    textAlign: "center",
    padding: "12px 16px",
    backgroundColor: "#032B4E",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    whiteSpace: "nowrap",
    border: "1px solid #032B4E",
  },
  tr: {
    transition: "background-color 0.15s ease",
  },
  td: {
    padding: "12px 16px",
    textAlign: "center",
    color: "#6a7d8c",
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    border: "1px solid #e2eaf0",
    whiteSpace: "nowrap",
    background: "#fff",
  },
  tdBold: {
    padding: "12px 16px",
    textAlign: "center",
    color: "#0c3c54",
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    border: "1px solid #e2eaf0",
    whiteSpace: "nowrap",
    background: "#fff",
  },
  tdDesc: {
    padding: "12px 16px",
    textAlign: "left",
    color: "#6a7d8c",
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    border: "1px solid #e2eaf0",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    background: "#fff",
  },
  tdCenter: {
    padding: "12px 16px",
    textAlign: "center",
    color: "#6a7d8c",
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    border: "1px solid #e2eaf0",
    background: "#fff",
  },
  badge: {
    display: "inline-block",
    minWidth: 72,
    padding: "5px 10px",
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#ffffff",
    fontFamily: "Poppins, sans-serif",
  },
  detailsLink: {
    padding: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#E06639",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "Poppins, sans-serif",
  },
  emptyCell: {
    color: "#9ca3af",
  },
  emptyRow: {
    padding: "28px 16px",
    textAlign: "center",
    color: "#6a7d8c",
    fontSize: 14,
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
  },
  pagination: {
    marginTop: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  pageBtn: {
    minWidth: 40,
    padding: "8px 14px",
    border: "1px solid #e6eef4",
    borderRadius: 8,
    background: "#fff",
    color: "#0c3c54",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
  },
  pageBtnActive: {
    background: "#E06639",
    color: "#fff",
    borderColor: "#E06639",
    boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
  },
};

export default SupplyChainList;
